/**
 * O que cada página diz de si para quem não a lê com os olhos.
 *
 * **Três públicos, um texto só.** O buscador lê `<title>`, `description` e o
 * `canonical`; o robô de prévia do WhatsApp lê as `og:`; o modelo de linguagem
 * que responde "onde baixo a prova do Enac" lê o corpo e os dados estruturados.
 * Todos os três recebem o mesmo conteúdo, e é por isso que este módulo existe:
 * **antes dele, a resposta dependia de onde a pergunta chegava** - o
 * `index.html` respondia uma coisa, a função da Vercel outra, e a SPA não
 * respondia nada.
 *
 * **Ele é isomórfico, e isso é uma restrição, não um enfeite.** O mesmo arquivo
 * é lido pelo navegador (via `useMeta`) e pela função da Vercel (via
 * `api/pagina.ts`), então aqui não entra React, não entra `document` e não
 * entra `@titularis/ui`: pacote de workspace exporta TypeScript cru, que a
 * Vercel copiaria como `.ts` para o runtime - o `api/pagina.ts` conta essa
 * história por inteiro.
 *
 * Quem escreve o texto de cada página é o `paginas.ts`, ao lado. Aqui mora só
 * a forma: o que vira tag, como se escapa e como o documento é montado.
 */

/**
 * O endereço oficial do site.
 *
 * **É `www`, e isso foi conferido contra a produção, não escolhido aqui.** A
 * Vercel responde 308 de `titularis.com.br` para `www.titularis.com.br`, e até
 * 08/09/2026 o canônico do `index.html` apontava para o apex - ou seja, cada
 * página servida no `www` declarava como oficial um endereço que redireciona
 * de volta para ela. O buscador desembaraça esse laço quase sempre, e "quase
 * sempre" é uma aposta que não precisa existir.
 *
 * Trocar o domínio principal é trocar esta linha **e** o redirecionamento na
 * Vercel, nessa ordem.
 */
export const SITE = 'https://www.titularis.com.br';

/** Notícias e artigos da revista usam `article`; as coleções usam `website`. */
export type TipoDeMeta = 'website' | 'article';

export interface Meta {
  /** O que a aba e o cartão anunciam, **sem** o sufixo da marca. */
  titulo: string;
  descricao: string;
  /** Endereço canônico da página, absoluto e no domínio que serve o site. */
  url: string;
  /** A imagem do cartão, absoluta e já em 1200x630. */
  imagem: string;
  /** Texto alternativo da imagem, para quem lê o cartão por leitor de tela. */
  imagemAlt: string;
  tipo: TipoDeMeta;
  /** Data de notícia ou artigo: vira `article:published_time`. */
  publicadaEm?: string;
  /**
   * Fora do índice.
   *
   * Vale para o endereço que não existe e para a listagem filtrada que não
   * devolveu nada: página vazia indexada não traz ninguém e ainda ensina o
   * buscador que o site tem páginas vazias.
   */
  foraDoIndice?: boolean;
  /** Dados estruturados, um objeto por `<script type="application/ld+json">`. */
  jsonLd?: Record<string, unknown>[];
}

/**
 * Uma tag do `<head>`, ainda como dado.
 *
 * **É o que permite haver um lugar só.** O servidor rende isto como texto e o
 * navegador rende isto como nó do DOM; se cada lado montasse a própria lista,
 * a página nova entraria num deles e seria esquecida no outro - que é
 * exatamente o defeito que este módulo veio consertar.
 */
export type Etiqueta =
  | { tag: 'title'; texto: string }
  | { tag: 'meta'; chave: 'name' | 'property'; nome: string; conteudo: string }
  | { tag: 'link'; rel: string; href: string }
  | { tag: 'jsonld'; conteudo: string };

/**
 * Escapa para caber dentro de um atributo HTML entre aspas duplas.
 *
 * É a única defesa do documento: título e chamada saem do editor, e um título
 * com aspas - "Provimento 190: o que muda no "prenotado"" - fecharia o
 * `content=` no meio e derrubaria todas as tags dali para baixo. O cartão não
 * quebraria com erro: viria vazio.
 */
export const escapar = (texto: string): string =>
  texto
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * O limite a partir do qual o Google corta o título na busca.
 *
 * Não é uma regra do protocolo, é a largura do resultado. O que passa disso
 * não some do índice, mas some da tela - e o pedaço cortado costuma ser o fim,
 * que é justo onde o sufixo da marca fica.
 */
const LIMITE_DO_TITULO = 60;

/**
 * O título da aba: o da página, com a marca atrás **quando ela cabe**.
 *
 * O nome de uma prova já é longo ("3º Exame Nacional dos Cartórios - Enac,
 * 2026.1"), e empurrar "· Titularis" para o fim de um título que o buscador já
 * ia cortar gasta os caracteres que sobraram com a palavra que menos informa
 * quem está procurando prova.
 */
export const tituloDaAba = (titulo: string): string => {
  if (titulo.includes('Titularis')) return titulo;

  const completo = `${titulo} · Titularis`;
  return completo.length > LIMITE_DO_TITULO ? titulo : completo;
};

/**
 * As tags do `<head>` desta página.
 *
 * A ordem importa para quem lê o documento por cima - `<title>` e `canonical`
 * primeiro -, e as três famílias existem porque os robôs não leem as mesmas
 * coisas: WhatsApp e Facebook leem Open Graph, o X lê as `twitter:`, e o resto
 * (Slack, Discord, buscador) cai no `<title>` e no `description`.
 */
export function etiquetasDe(meta: Meta): Etiqueta[] {
  const etiquetas: Etiqueta[] = [
    { tag: 'title', texto: tituloDaAba(meta.titulo) },
    { tag: 'meta', chave: 'name', nome: 'description', conteudo: meta.descricao },
    { tag: 'link', rel: 'canonical', href: meta.url },
  ];

  if (meta.foraDoIndice) {
    /*
     * `noindex, follow`: a página não entra no índice, mas os links dela
     * continuam valendo - é o que faz o buscador que caiu num filtro vazio
     * seguir para as provas que existem em vez de dar meia-volta.
     */
    etiquetas.push({ tag: 'meta', chave: 'name', nome: 'robots', conteudo: 'noindex, follow' });
  }

  etiquetas.push(
    { tag: 'meta', chave: 'property', nome: 'og:type', conteudo: meta.tipo },
    { tag: 'meta', chave: 'property', nome: 'og:locale', conteudo: 'pt_BR' },
    { tag: 'meta', chave: 'property', nome: 'og:site_name', conteudo: 'Titularis' },
    { tag: 'meta', chave: 'property', nome: 'og:title', conteudo: meta.titulo },
    { tag: 'meta', chave: 'property', nome: 'og:description', conteudo: meta.descricao },
    { tag: 'meta', chave: 'property', nome: 'og:url', conteudo: meta.url },
    { tag: 'meta', chave: 'property', nome: 'og:image', conteudo: meta.imagem },
    /*
     * O tipo e a medida vão declarados porque o WhatsApp decide entre cartão
     * grande e miniatura **antes** de baixar a imagem: sem eles ele chuta, e o
     * chute é a miniatura.
     */
    { tag: 'meta', chave: 'property', nome: 'og:image:type', conteudo: 'image/jpeg' },
    { tag: 'meta', chave: 'property', nome: 'og:image:width', conteudo: '1200' },
    { tag: 'meta', chave: 'property', nome: 'og:image:height', conteudo: '630' },
    { tag: 'meta', chave: 'property', nome: 'og:image:alt', conteudo: meta.imagemAlt },

    { tag: 'meta', chave: 'name', nome: 'twitter:card', conteudo: 'summary_large_image' },
    { tag: 'meta', chave: 'name', nome: 'twitter:title', conteudo: meta.titulo },
    { tag: 'meta', chave: 'name', nome: 'twitter:description', conteudo: meta.descricao },
    { tag: 'meta', chave: 'name', nome: 'twitter:image', conteudo: meta.imagem },
  );

  if (meta.publicadaEm) {
    etiquetas.push({
      tag: 'meta',
      chave: 'property',
      nome: 'article:published_time',
      conteudo: meta.publicadaEm,
    });
  }

  for (const dado of meta.jsonLd ?? []) {
    etiquetas.push({ tag: 'jsonld', conteudo: JSON.stringify(dado) });
  }

  return etiquetas;
}

/**
 * O `</script>` dentro do JSON, que é o furo clássico do dado estruturado.
 *
 * O conteúdo do `<script>` não é escapado como atributo - o navegador procura
 * a sequência de fechamento e para ali. Um título de notícia que contenha
 * `</script>` encerraria o bloco no meio do JSON, e o resto do objeto viraria
 * HTML solto no meio da página.
 */
const escaparJson = (json: string): string => json.replace(/<\/(script)/gi, '<\\/$1');

/** As tags como texto, para quem monta o documento no servidor. */
export const montarTags = (meta: Meta): string[] =>
  etiquetasDe(meta).map((etiqueta) => {
    switch (etiqueta.tag) {
      case 'title':
        return `<title>${escapar(etiqueta.texto)}</title>`;
      case 'meta':
        return `<meta ${etiqueta.chave}="${etiqueta.nome}" content="${escapar(etiqueta.conteudo)}" />`;
      case 'link':
        return `<link rel="${etiqueta.rel}" href="${escapar(etiqueta.href)}" />`;
      case 'jsonld':
        return `<script type="application/ld+json">${escaparJson(etiqueta.conteudo)}</script>`;
    }
  });

/**
 * O documento inteiro, para o robô que não roda JavaScript.
 *
 * **O corpo não é enfeite.** Enquanto ele era só a chamada da notícia, mandar o
 * Google para cá pioraria o que ele indexa - era o argumento que mantinha o
 * buscador fora desta função. Agora o corpo é a página: o texto da notícia, a
 * ficha da prova, a lista com os links. Com conteúdo equivalente ao da SPA, o
 * robô que não renderiza JavaScript passa a ler o mesmo que o aluno lê.
 */
export function montarDocumento(meta: Meta, corpo: string): string {
  return `<!doctype html>
<html lang="pt-BR">
<head>
${montarTags(meta)
  .map((tag) => `  ${tag}`)
  .join('\n')}
</head>
<body>
${corpo}
</body>
</html>
`;
}
